import React, { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase/client';
import { Loader2, FileText } from 'lucide-react';
import ListTextarea from '../../../components/ui/ListTextarea';
import KiTextHelfer from '../../../components/ki/KiTextHelfer';
import { useIslandActions } from '../../../components/island/IslandProvider';
import { usePillenSteuerung } from '../../../components/island/widgets/SteuerungsAnsicht';
import type { SteuerungsEintrag } from '../../../components/island/widgets/typen';
import { Archive, ArrowCounterClockwise, CalendarPlus, FlowArrow, LinkSimple, Paperclip, FilePdf, FileText as FileTextIcon, FloppyDisk, PencilSimple, Wrench as WrenchIcon, X as XIcon } from '@phosphor-icons/react';
import { exportSingleTicketPdf } from '../../../lib/export/exportTicketPdf';
import { exportZeitnachweisPdf } from '../../../lib/export/exportZeitnachweisPdf';
import { ladeTicketZeiten } from '../../../lib/zeiterfassung/api';
import EmailVerfassen from '../emails/EmailVerfassen';
import { ladeAuftragsDaten } from '../../../lib/tickets/ladeAuftragsDaten';
import { auftragsBetreffObjekt, baueAuftragsMail, auftragsPlatzhalterWerte, type AuftragsDaten, type AuftragsArt } from '../../../lib/tickets/auftragsMail';
import AuftragVorbereitenModal, { type DienstleisterOption, type EinheitOption, type MeldenderOption } from './AuftragVorbereitenModal';
import { ladeMeldenderKandidaten } from '../../../lib/tickets/meldenderKandidaten';
import {
  ladeTicketTermine, loeseTicketTermin, naechsterTermin, protokolliereTermin, type TicketTermin,
} from '../../../lib/tickets/terminLink';
import TerminFormular from '../kalender/TerminFormular';
import { setzeAktionStatus } from '../../../lib/aktionen/api';
import { verknuepfeTicketMitEmail, ladeEmailsZuTicket, loeseTicketEmailVerknuepfung } from '../../../lib/ki/ticketLink';
import TicketKiPanel from './TicketKiPanel';
import TicketAnhaenge from './TicketAnhaenge';
import TicketVerknuepfungen from './TicketVerknuepfungen';
import TicketComposer from './TicketComposer';
import TicketVerlaufThread from './TicketVerlaufThread';
import { useTicketBearbeitung } from './useTicketBearbeitung';
import type { Ticket, Kategorie, TicketEditData } from './types';
import { CaretLeft, Trash as TrashIcon } from '@phosphor-icons/react';
import { BestaetigenDialog, SchwebenderToast, useToastPill } from '../../../components/immobilien/ui';
import { GlasTooltipSchicht } from '../../../components/ui/GlasTooltip';
import { useViewport } from '../../../components/island/hooks';
import { useMobilModus } from '../../../lib/immobilien/mobil';
import { istZweispaltig, werkzeugNurIcons, type HauptAktionArt } from '../../../lib/tickets/darstellung';
import { DetailKopf } from './detail/DetailKopf';
import { WidgetStatus } from './detail/WidgetStatus';
import { WidgetFristen } from './detail/WidgetFristen';
import { WidgetVerknuepft } from './detail/WidgetVerknuepft';
import { WidgetEinsatz } from './detail/WidgetEinsatz';
import { WidgetEigentuemerPortal } from './detail/WidgetEigentuemerPortal';
import { useTicketNamen } from './detail/widgetTeile';
import { WidgetHuelle } from '../../../components/ui/WidgetHuelle';
import VorgangWidget, { hatVorgang } from './vorgaenge/VorgangWidget';
import { AbschlussDialog } from './AbschlussDialog';
import { MAIL_FORM_META } from '../../../components/island/form/mailMeta';

export interface TicketDetailSeiteProps {
  ticketId: string;
  userProfile?: any;
  onBack: () => void;                       // navigate('/admin/tickets')
  onNavigateToObjekt?: (id: string) => void;
  onNavigateToMieter?: (id: string) => void;
  /** Sprung in den Kalender (Ticket → Termin). */
  onNavigateToTermin?: (id: string) => void;
  onMutated?: () => void;                   // Liste refetchen nach Mutationen (Sync mit TicketListe)
}

// Voll-Seiten-Ticketdetail (Task 7). Setzt die in Task 1–6 extrahierten Bausteine zu einer
// 2-Spalten-Seite zusammen (Thread + Composer in der Mitte, Meta rechts). Die kritische
// Edit/Save/Status-Logik teilt sich diese Seite über useTicketBearbeitung mit dem alten Panel
// (TicketDetailsSidebar) — es wird NICHTS an den Reads/Writes geändert. Handwerker beauftragen,
// PDF-Export und Archivieren/Wiederherstellen sind aus dem Panel gespiegelt (temporäre Kopie;
// das Panel wird in Task 9 entfernt) — siehe .superpowers/sdd/task-7-report.md.

// ─── Datenschale (lädt Ticket + Stammdaten, hält Hooks außerhalb des Loading-Guards) ──────────
export default function TicketDetailSeite({
  ticketId, userProfile, onBack, onNavigateToObjekt, onNavigateToMieter, onNavigateToTermin, onMutated,
}: TicketDetailSeiteProps) {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [kategorien, setKategorien] = useState<Kategorie[]>([]);
  const [teamMembers, setTeamMembers] = useState<{ id: string; full_name: string }[]>([]);
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  // Ticket laden — dieselben Joins wie TicketListe.fetchTickets (FK-Hints für kategorie/zustaendig
  // sind zwingend, sonst wirft PostgREST "more than one relationship"). maybeSingle: fehlende Zeile
  // (= gelöscht/ungültige id) → onBack().
  const reloadTicket = useCallback(async () => {
    const { data, error } = await supabase
      .from('tickets')
      .select(`
        *,
        kategorie:ticket_kategorien!tickets_kategorie_id_fkey(id, name, farbe, parent_id),
        objekt:objekte(id, bezeichnung, strasse, hausnummer),
        mieter_rel:mieter(id, vorname, nachname),
        eigentuemer_rel:eigentuemer(id, vorname, nachname, firma_name)
      `)
      .eq('id', ticketId)
      .maybeSingle();
    if (error || !data) { onBack(); return; }
    setTicket(data as Ticket);
    setLoading(false);
  }, [ticketId, onBack]);

  useEffect(() => {
    setLoading(true);
    reloadTicket();
    // Stammdaten wie im Panel/TicketListe
    supabase.from('ticket_kategorien').select('*').eq('aktiv', true).order('sortierung')
      .then(({ data }) => setKategorien((data as Kategorie[]) || []));
    supabase.from('profiles').select('id, full_name').in('role', ['admin', 'team', 'super admin']).eq('zuweisbar', true)
      .then(({ data }) => setTeamMembers(data || []));
    supabase.from('teams').select('id, name').order('name')
      .then(({ data }) => setTeams(data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  if (loading || !ticket) {
    return (
      <div className="flex items-center justify-center py-24" style={{ color: 'var(--ink3)' }}>
        <Loader2 size={22} className="animate-spin" />
      </div>
    );
  }

  return (
    <TicketDetailInner
      ticket={ticket}
      kategorien={kategorien}
      teamMembers={teamMembers}
      teams={teams}
      userProfile={userProfile}
      onBack={onBack}
      onNavigateToObjekt={onNavigateToObjekt}
      onNavigateToMieter={onNavigateToMieter}
      onNavigateToTermin={onNavigateToTermin}
      onMutated={onMutated}
      reloadTicket={reloadTicket}
    />
  );
}

// ─── Innenteil: garantiert non-null Ticket, damit useTicketBearbeitung/usePillenSteuerung
//     bedingungsfrei laufen (Rules of Hooks). ────────────────────────────────────────────────
interface InnerProps {
  ticket: Ticket;
  kategorien: Kategorie[];
  teamMembers: { id: string; full_name: string }[];
  teams: { id: string; name: string }[];
  userProfile?: any;
  onBack: () => void;
  onNavigateToObjekt?: (id: string) => void;
  onNavigateToMieter?: (id: string) => void;
  onNavigateToTermin?: (id: string) => void;
  onMutated?: () => void;
  reloadTicket: () => void | Promise<void>;
}

function TicketDetailInner({
  ticket, kategorien, teamMembers, teams, userProfile, onBack, onNavigateToObjekt, onNavigateToMieter,
  onNavigateToTermin, onMutated, reloadTicket,
}: InnerProps) {
  // Remount-Key für den Thread (TicketVerlaufThread re-fetcht nur bei ticket.id-Wechsel; nach
  // Kommentaren/Statuswechseln bleibt die id gleich → gezielter Remount, gleiches Muster wie der
  // kommentareRefreshKey im Panel).
  const [threadKey, setThreadKey] = useState(0);

  // Jede Mutation refetcht das Ticket UND remountet den Thread.
  const reload = useCallback(() => {
    setThreadKey((k) => k + 1);
    reloadTicket();
    onMutated?.();          // Liste im Hintergrund frisch halten (Sync mit TicketListe)
  }, [reloadTicket, onMutated]);

  // Geteilte Edit/Save/Status-Orchestrierung (siehe useTicketBearbeitung.ts / task-6-report.md).
  const b = useTicketBearbeitung(ticket, reload, userProfile);

  // Verknüpfte E-Mails (aus entity_links + quelle_email_id) — manuell aufhebbar.
  const [verknuepfteEmails, setVerknuepfteEmails] = useState<{ id: string; betreff: string | null; von: string | null; datum: string | null }[]>([]);
  const ladeEmails = useCallback(async () => {
    setVerknuepfteEmails(await ladeEmailsZuTicket(ticket.id));
  }, [ticket.id]);
  useEffect(() => { ladeEmails(); }, [ladeEmails, ticket.quelle_email_id]);
  // Verknüpfte Termine (entity_links ticket → termin). Der Termin bleibt ein eigenständiges
  // Kalenderobjekt — hier hängt nur die Verbindung.
  const [termine, setTermine] = useState<TicketTermin[]>([]);
  const [termineLaden, setTermineLaden] = useState(true);
  const ladeTermine = useCallback(async () => {
    setTermineLaden(true);
    setTermine(await ladeTicketTermine(ticket.id));
    setTermineLaden(false);
  }, [ticket.id]);
  useEffect(() => { void ladeTermine(); }, [ladeTermine]);
  const naechsterTerminId = naechsterTermin(termine, new Date().toISOString())?.id ?? null;

  const handleTerminAnlegen = () => {
    openForm(
      <TerminFormular
        variant="pille"
        editTermin={null}
        prefillDate={null}
        teamMembers={teamMembers}
        userProfile={userProfile}
        ticketId={ticket.id}
        prefillObjektId={ticket.objekt_id ?? null}
        prefillTitel={ticket.titel || ''}
        onClose={() => closeForm()}
        onSaved={() => { closeForm(); void ladeTermine(); reload(); }}
      />,
      'Termin zum Ticket',
      520,
      { icon: CalendarPlus, farbe: 'rot', badge: 'plus', pflichtHinweis: true },
    );
  };

  const handleTerminLoesen = async (t: TicketTermin) => {
    if (!confirm(`Verknüpfung zu „${t.titel}" aufheben? Der Termin bleibt im Kalender.`)) return;
    try {
      await loeseTicketTermin(ticket.id, t.id);
      await protokolliereTermin(ticket.id, 'termin_geloest', `Terminverknüpfung aufgehoben: ${t.titel}`, userProfile?.id);
      await ladeTermine();
      reload();
    } catch (e) {
      alert(`Verknüpfung konnte nicht aufgehoben werden: ${(e as Error).message}`);
    }
  };

  const loeseEmail = async (emailId: string) => {
    if (!confirm('Verknüpfung zu dieser E-Mail aufheben?')) return;
    try {
      await loeseTicketEmailVerknuepfung(ticket.id, emailId);
      await ladeEmails();
      reload();
    } catch (err: any) {
      alert('Aufheben fehlgeschlagen: ' + (err?.message || 'Unbekannt'));
    }
  };

  // Island-Formularbereich (Pille) — Handwerker-Beauftragung läuft dort in zwei Schritten.
  const { openForm, closeForm } = useIslandActions();

  // Archivieren (Soft-Delete) — Namen/Writes 1:1 aus dem Panel.
  const [showArchivConfirm, setShowArchivConfirm] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // Handwerker beauftragen — beide Schritte (Vorbereiten → Mail) laufen im
  // Pillen-Formularbereich (openForm), wie die Mieter-Nachricht und das E-Mail-Modul.
  const [beauftragenLaedt, setBeauftragenLaedt] = useState(false);

  // Namen, die das Ticket nur als id kennt (Ersteller, Vorschläge, Einheit)
  const namen = useTicketNamen(ticket);
  const einheitName = namen.einheit;
  const [anhangAnzahl, setAnhangAnzahl] = useState(0);

  // Kopfdaten des Zeitnachweises — dieselbe Quelle fuer den Knopf bei den Zeiten und
  // fuer die zweite Datei beim Ticket-Export.
  const nachweisKopf = {
    ticket_nummer: ticket.ticket_nummer,
    titel: ticket.titel,
    objekt: ticket.objekt,
    einheit: einheitName,
  };

  const istAbgeschlossen = ticket.status === 'erledigt' || ticket.status === 'geschlossen';
  const istArchiviert = !!ticket.geloescht_am;

  // ── PDF-Export (1:1 aus dem Panel: Kommentare + Historie + Anhänge on-demand laden) ──────────
  const handleExportPdf = async () => {
    try {
      const [komRes, histRes, anhaengeRes] = await Promise.all([
        supabase.from('ticket_kommentare')
          .select('*, autor:profiles!ticket_kommentare_autor_id_fkey(full_name)')
          .eq('ticket_id', ticket.id)
          .is('parent_id', null)
          .order('created_at', { ascending: true }),
        supabase.from('ticket_historie')
          .select('*, benutzer:profiles!ticket_historie_benutzer_id_fkey(full_name)')
          .eq('ticket_id', ticket.id)
          .order('created_at', { ascending: true }),
        supabase.from('ticket_anhaenge')
          .select('dateiname, dateigroesse, created_at')
          .eq('ticket_id', ticket.id)
          .order('created_at', { ascending: false }),
      ]);
      exportSingleTicketPdf(
        ticket,
        komRes.data || [],
        (histRes.data || []).filter((h: any) => h.aktion !== 'kommentar'),
        anhaengeRes.data || []
      );

      // Der Zeitnachweis kommt als ZWEITE Datei — bewusst nicht im Ticket-PDF, damit man
      // ihn an den Kunden weiterreichen kann, ohne dass dieser den Vorgang mitliest.
      // Ohne abrechenbare Zeiten entsteht gar keine Datei.
      const zeiten = await ladeTicketZeiten(ticket.id);
      const { data: leute } = await supabase
        .from('profiles').select('id, full_name')
        .in('id', [...new Set(zeiten.map((z) => z.mitarbeiter_id))]);
      const namen = Object.fromEntries((leute || []).map((p) => [p.id as string, (p.full_name as string) || '']));
      exportZeitnachweisPdf(nachweisKopf, zeiten, namen);
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  // ── Archivieren / Wiederherstellen (1:1 aus dem Panel; statt onClose() bleibt die Seite und
  //    refetcht via reload(), damit der jeweils andere Zustand direkt verfügbar ist) ───────────
  const handleArchive = async () => {
    setArchiving(true);
    try {
      await supabase.from('tickets').update({ geloescht_am: new Date().toISOString() }).eq('id', ticket.id);
      await supabase.from('ticket_historie').insert({
        ticket_id: ticket.id,
        benutzer_id: userProfile?.id,
        aktion: 'archiviert',
        neuer_wert: 'Ticket in den Papierkorb verschoben',
      });
      setShowArchivConfirm(false);
      reload();
    } catch (err) {
      console.error('Error archiving ticket:', err);
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async () => {
    setArchiving(true);
    try {
      await supabase.from('tickets').update({ geloescht_am: null }).eq('id', ticket.id);
      await supabase.from('ticket_historie').insert({
        ticket_id: ticket.id,
        benutzer_id: userProfile?.id,
        aktion: 'wiederhergestellt',
        neuer_wert: 'Ticket aus dem Papierkorb wiederhergestellt',
      });
      reload();
    } catch (err) {
      console.error('Error restoring ticket:', err);
    } finally {
      setArchiving(false);
    }
  };

  // ── Handwerker beauftragen / Angebot anfragen ───────────────────────────────────────────────
  // Beide Wege sind derselbe Ablauf (Vorbereiten → Mail); nur Vorlage, Betreff und der
  // Historien-Eintrag unterscheiden sich. Vorher gab es nur die Beauftragung — wer ein Angebot
  // wollte, musste im Mail-Fenster die Vorlage umstellen und stand vor leeren Feldern.
  const handleBeauftragen = async (art: AuftragsArt = 'auftrag') => {
    // #564 T4: Hat der Eigentümer die Kosten abgelehnt, ist der Auftrag gesperrt – VOR dem Mailfenster, damit keine
    // Auftrags-Mail rausgeht. Ein neues Angebot anfordern bleibt möglich.
    if (art === 'auftrag') {
      const { data: k } = await supabase.from('tickets').select('kosten_entscheidung, kosten_begruendung').eq('id', ticket.id).maybeSingle();
      if (k?.kosten_entscheidung === 'abgelehnt') {
        alert(`Der Eigentümer hat die Kosten abgelehnt${k.kosten_begruendung ? ` („${k.kosten_begruendung}“)` : ''}.\n\nBitte zuerst klären, z. B. ein neues Angebot anfordern, und die Entscheidung im Widget „Eigentümerportal“ zurücksetzen.`);
        return;
      }
    }
    setBeauftragenLaedt(true);
    try {
      const { daten, anhaenge, objektId, einheitId } = await ladeAuftragsDaten(ticket.id);
      const { data: konto } = await supabase
        .from('email_konten').select('id, email_adresse, bezeichnung').eq('aktiv', true).limit(1).maybeSingle();
      if (!konto) { alert('Kein E-Mail-Konto konfiguriert.'); return; }
      const [dlRes, ehRes, kandidaten] = await Promise.all([
        supabase.from('dienstleister')
          .select('id, name, ansprechpartner, email, telefon, ist_bevorzugt')
          .eq('aktiv', true).order('ist_bevorzugt', { ascending: false }).order('name'),
        objektId
          ? supabase.from('objekt_einheiten').select('id, bezeichnung').eq('objekt_id', objektId).order('bezeichnung')
          : Promise.resolve({ data: [] as EinheitOption[] }),
        ladeMeldenderKandidaten(objektId, daten.meldender, einheitId),
      ]);
      const ctx = {
        konto: { id: konto.id, email_adresse: konto.email_adresse, bezeichnung: konto.bezeichnung || konto.email_adresse },
        anhaenge,
      };
      // Schritt 1 in der Pille: vorbereiten (Handwerker/Einheit/Beschreibung/Meldender)
      openForm(
        <AuftragVorbereitenModal
          variant="pille"
          art={art}
          daten={daten}
          einheiten={(ehRes.data as EinheitOption[]) || []}
          einheitId={einheitId}
          dienstleister={(dlRes.data as DienstleisterOption[]) || []}
          meldenderKandidaten={kandidaten as MeldenderOption[]}
          onClose={() => closeForm()}
          onWeiter={(datenNeu) => handleAuftragWeiter(ctx, datenNeu, art)}
        />,
        art === 'angebot' ? 'Angebotsanfrage vorbereiten' : 'Auftrag vorbereiten',
        640,
        { icon: WrenchIcon, farbe: 'gelb', badge: 'pfeil' },
      );
    } catch (e) { console.error(e); alert('Auftragsdaten konnten nicht geladen werden.'); }
    finally { setBeauftragenLaedt(false); }
  };

  // Aus dem Dienstleister-Bereich („Auftrag senden“ / „Angebot anfordern“): ?beauftragen=auftrag|angebot
  // startet den Versand einmal; der Parameter wird danach entfernt.
  const [suchParameter, setSuchParameter] = useSearchParams();
  const beauftragenWunsch = suchParameter.get('beauftragen');
  // Aus der Timer-Pille: ?einsatz=zeiten öffnet den Tab Zeiten und scrollt zum Einsatz-Widget.
  const einsatzWunsch = suchParameter.get('einsatz');
  const [einsatzSprung, setEinsatzSprung] = useState(0);
  useEffect(() => {
    if (einsatzWunsch !== 'zeiten') return;
    setSuchParameter(p => { const n = new URLSearchParams(p); n.delete('einsatz'); return n; }, { replace: true });
    setEinsatzSprung(z => z + 1);
    requestAnimationFrame(() => document.querySelector('section[aria-label="Einsatz"]')?.scrollIntoView({ block: 'start', behavior: 'smooth' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [einsatzWunsch]);
  useEffect(() => {
    if (beauftragenWunsch !== 'auftrag' && beauftragenWunsch !== 'angebot') return;
    setSuchParameter(p => { const n = new URLSearchParams(p); n.delete('beauftragen'); return n; }, { replace: true });
    void handleBeauftragen(beauftragenWunsch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beauftragenWunsch]);

  // Schritt 2 in der Pille: Auftrags-Mail (openForm ersetzt den Vorbereiten-Inhalt).
  // Die ticket_aktionen-Zeile wird VOR dem Öffnen angelegt, damit die aktionId als lokale
  // Variable im onSent-Closure landet (kein Stale-State-Snapshot im openForm-Element).
  const handleAuftragWeiter = async (
    ctx: { konto: { id: string; email_adresse: string; bezeichnung: string }; anhaenge: { id: string; name: string; size: number; path: string }[] },
    datenNeu: AuftragsDaten,
    art: AuftragsArt = 'auftrag',
  ) => {
    const angebot = art === 'angebot';
    const mail = baueAuftragsMail(datenNeu, art);
    const aktionsTyp = angebot ? 'angebot_anfordern' : 'handwerker_beauftragen';

    // Best effort: manueller Flow wird Engine-Bürger (Entwurf in ticket_aktionen; Status wechselt
    // beim Versand auf 'gesendet'). Vorherige MANUELLE Entwürfe dieses Tickets ersetzen —
    // sonst sammelt jeder "Weiter zur Mail"-Klick einen weiteren Entwurf im KI-Panel an.
    let aktionId: string | null = null;
    try {
      await supabase.from('ticket_aktionen').delete()
        .eq('ticket_id', ticket.id).eq('typ', aktionsTyp)
        .eq('status', 'entwurf').eq('ki_generiert', false);
      const { data } = await supabase.from('ticket_aktionen').insert({
        ticket_id: ticket.id,
        typ: aktionsTyp,
        status: 'entwurf',
        freigabe_erforderlich: true,
        ist_rechtlich_sensibel: false,
        empfaenger: mail.empfaenger || null,
        betreff: mail.betreff,
        entwurf_text: mail.text,
        payload: { anhaenge: ctx.anhaenge },
        ki_generiert: false,
        erstellt_von: userProfile?.id ?? null,
      }).select('id').single();
      aktionId = data?.id ?? null;
    } catch (e) {
      console.warn('ticket_aktionen-Zeile für Beauftragung konnte nicht angelegt werden', e);
    }

    openForm(
      <EmailVerfassen
        variant="pille"
        konto={ctx.konto}
        draft={{
          an: mail.empfaenger ? [mail.empfaenger] : [],
          // Fallback-Betreff — die Auto-Vorlage überschreibt ihn mit ihrem betreff_template.
          betreff: mail.betreff,
          // Auftragstext NICHT über draft.text_inhalt (rendert doppelt: roh + im Bogen).
          text_inhalt: '',
          anhaenge: ctx.anhaenge.map((a) => ({ name: a.name, path: a.path, size: a.size })),
        }}
        // Quelle des Inhalts: E-Mail-Vorlage „Handwerkerbeauftragung" (vorausgewählt),
        // Platzhalter aus den Ticket-/Auftragsdaten. autoText bleibt Fallback, falls die
        // Vorlage gelöscht/inaktiv ist.
        autoVorlageTrigger={angebot ? 'angebot_anfordern' : 'handwerker_beauftragung'}
        // #525: Objekt in den Betreff („Angebotsanfrage für Handwerkerleistungen – Treiser Straße 18+20, Staufenberg“)
        betreffZusatz={auftragsBetreffObjekt(datenNeu)}
        autoVorlageWerte={auftragsPlatzhalterWerte(datenNeu)}
        autoText={mail.text}
        onClose={() => closeForm()}
        onSent={async (info) => {
          closeForm();
          try {
            // beauftragter Dienstleister am Ticket (Aufträge & Tickets im Dienstleister-Bereich, Reaktionszeit)
            await supabase.from('tickets').update({
              status: 'warten_auf_dienstleister',
              ...(datenNeu.dienstleister?.id ? { dienstleister_id: datenNeu.dienstleister.id, beauftragt_am: new Date().toISOString() } : {}),
            }).eq('id', ticket.id);
            // Alle tatsächlich angeschriebenen Adressen protokollieren — bei einer
            // Sammelanfrage stehen die Betriebe im Bcc und wären sonst nicht mehr
            // nachvollziehbar (User-Meldung 2026-08-05).
            const angeschrieben = [...(info?.an ?? []), ...(info?.cc ?? []), ...(info?.bcc ?? [])];
            await supabase.from('ticket_historie').insert({
              ticket_id: ticket.id, benutzer_id: userProfile?.id, aktion: angebot ? 'angebot_angefragt' : 'beauftragt',
              neuer_wert: `${angebot ? 'Angebot angefragt bei' : 'Handwerker beauftragt:'} ${
                angeschrieben.length ? angeschrieben.join(', ') : (datenNeu.dienstleister?.name || mail.empfaenger || '')
              }`.trim(),
            });
            // Gesendete Mail ans Ticket hängen → erscheint als Thread-Eintrag (mit Briefpapier
            // und Anhängen). Volltext-Spiegel-Kommentar nur als Fallback ohne emailId.
            let emailVerknuepft = false;
            if (info?.emailId) {
              try {
                await verknuepfeTicketMitEmail(ticket.id, info.emailId);
                emailVerknuepft = true;
              } catch (e) { console.warn('Gesendete Mail konnte nicht verknüpft werden', e); }
            }
            if (!emailVerknuepft) {
              const anhangZeile = info?.anhaenge?.length
                ? `\n\nAnhänge: ${info.anhaenge.map((a) => a.name).join(', ')}`
                : '';
              await supabase.from('ticket_kommentare').insert({
                ticket_id: ticket.id, autor_id: userProfile?.id, inhalt: `Auftrag an Handwerker gesendet:\n\n${info?.text || mail.text}${anhangZeile}`, ist_intern: true,
              });
            }
          } catch (e) { console.warn('Ticket-Nachbereitung nach Beauftragung fehlgeschlagen', e); }
          if (aktionId) {
            try { await setzeAktionStatus(aktionId, 'gesendet', userProfile?.id ?? null); }
            catch (e) { console.warn('Aktions-Status nach Beauftragung konnte nicht gesetzt werden', e); }
          }
          reload();
        }}
      />,
      'Handwerker beauftragen',
      640, MAIL_FORM_META,
    );
  };

  // Composer-Nachbereitung (Thread-Remount + Mini-Timeline-Refresh + Parent-Refetch — reload()
  // deckt Remount+Refetch ab, fetchMiniHistorie für die Hook-Timeline).
  // Neuestes steht oben (Ticket #501): nach dem Absenden dorthin springen, sonst
  // erscheint der eigene Eintrag ausserhalb des sichtbaren Bereichs.
  const verlaufRef = useRef<HTMLDivElement>(null);
  const handleComposerSent = () => {
    b.fetchMiniHistorie();
    reload();
    verlaufRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Pillen-Steuerung (Widgets, Handoff Pille §6) ─────────────────────────────────────────────
  const pillenEintraege: SteuerungsEintrag[] = b.editing
    ? [
        { id: 'speichern', typ: 'aktion', farbe: 'gold', icon: FloppyDisk, kicker: 'Ticket', titel: 'Speichern', unter: 'Änderungen übernehmen',
          laeuft: b.saving, onClick: () => { void b.handleSave(); } },
        { id: 'abbrechen', typ: 'aktion', farbe: 'grau', icon: XIcon, kicker: 'Ticket', titel: 'Abbrechen', unter: 'Bearbeiten beenden',
          onClick: () => b.setEditing(false) },
      ]
    : [
        { id: 'bearbeiten', typ: 'aktion', farbe: 'gold', icon: PencilSimple, kicker: 'Ticket', titel: 'Bearbeiten', unter: 'Bearbeiten-Modus',
          tooltip: 'Ticket bearbeiten', onClick: () => b.setEditing(true) },
        { id: 'angebot', typ: 'aktion', farbe: 'blau', icon: FileTextIcon, kicker: 'Dienstleister', titel: 'Angebot anfragen', unter: 'Anfrage vorbereiten',
          laeuft: beauftragenLaedt, onClick: () => { void handleBeauftragen('angebot'); } },
        { id: 'auftrag', typ: 'aktion', farbe: 'orange', icon: WrenchIcon, kicker: 'Dienstleister', titel: 'Handwerker beauftragen', unter: 'Auftrag vorbereiten',
          laeuft: beauftragenLaedt, onClick: () => { void handleBeauftragen('auftrag'); } },
        { id: 'pdf', typ: 'aktion', farbe: 'gruen', icon: FilePdf, kicker: 'Export', titel: 'PDF', unter: 'Ticket als PDF', onClick: () => { void handleExportPdf(); } },
        ...(istArchiviert
          ? [{ id: 'wiederherstellen', typ: 'aktion', farbe: 'grau', icon: ArrowCounterClockwise, kicker: 'Ablage', titel: 'Wiederherstellen', unter: 'Aus dem Archiv holen',
              laeuft: archiving, onClick: () => { void handleRestore(); } } satisfies SteuerungsEintrag]
          : istAbgeschlossen
            ? [{ id: 'archivieren', typ: 'aktion', farbe: 'grau', icon: Archive, kicker: 'Ablage', titel: 'Archivieren', unter: 'Mit Bestätigung',
                onClick: () => setShowArchivConfirm(true) } satisfies SteuerungsEintrag]
            : []),
      ];
  usePillenSteuerung(
    // ticket_nummer ist bereits sprechend („T-0166") — kein # davor.
    { titel: 'Ticket ' + ticket.ticket_nummer, zurueck: { label: 'Zur Ticketliste', onClick: onBack }, eintraege: pillenEintraege },
    [ticket.id, ticket.status, ticket.geloescht_am, b.editing, b.saving, beauftragenLaedt, archiving],
  );

  // ── Layout (Handoff §2): ab ~1250 px zwei Spalten ohne Seiten-Scroll ────────────────────────
  const viewport = useViewport();
  const { mobil } = useMobilModus();
  const zweispaltig = !mobil && istZweispaltig(viewport.width, viewport.height);
  const rasterRef = useRef<HTMLDivElement>(null);
  const [spaltenHoehe, setSpaltenHoehe] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!zweispaltig || !rasterRef.current) { setSpaltenHoehe(null); return; }
    const oben = rasterRef.current.getBoundingClientRect().top + window.scrollY;
    setSpaltenHoehe(Math.max(420, window.innerHeight - oben - 16));
  }, [zweispaltig, viewport.width, viewport.height]);
  // Seite bleibt oben (Spalten scrollen einzeln)
  useEffect(() => { if (zweispaltig) window.scrollTo(0, 0); }, [zweispaltig, ticket.id]);

  const [toast, zeigeToast] = useToastPill();
  // Bearbeiten direkt in den Widgets: gleicher Schreibweg wie Speichern, Fehler als Hinweis
  const speichereWidget = async (teil: Partial<TicketEditData>) => {
    const ok = await b.speichereFelder(teil);
    if (!ok) zeigeToast('Speichern fehlgeschlagen.', true);
  };
  const [aktionLaeuft, setAktionLaeuft] = useState(false);
  const heute = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();

  // Hauptaktion je Status (Handoff §3); Erledigt/Geschlossen laufen über den Abschluss-Dialog.
  const hauptaktion = async (art: HauptAktionArt, zielStatus: string | null) => {
    if (art === 'beauftragen') { void handleBeauftragen('auftrag'); return; }
    if (!zielStatus) return;
    if (art === 'schliessen') { b.handleStatusChange(zielStatus); return; }
    setAktionLaeuft(true);
    try {
      if (art === 'uebernehmen') {
        if (!userProfile?.id) return;
        const ok = await b.speichereFelder({ zustaendig_id: userProfile.id, zustaendig_typ: 'mitarbeiter' });
        if (!ok) { zeigeToast('Übernehmen fehlgeschlagen.', true); return; }
        await b.setzeStatus(zielStatus);
        zeigeToast(`${ticket.ticket_nummer} übernommen`);
        return;
      }
      await b.setzeStatus(zielStatus);
    } finally {
      setAktionLaeuft(false);
    }
  };

  const spalte = (inhalt: ReactNode, rechts = false): ReactNode => (
    <div className={`flex min-w-0 flex-col ${zweispaltig && rechts ? 'immo-scroll overflow-y-auto' : ''}`}
      style={{ gap: 12, ...(zweispaltig && spaltenHoehe ? { height: spaltenHoehe } : {}), ...(rechts && zweispaltig ? { paddingRight: 4 } : {}) }}>
      {inhalt}
    </div>
  );

  return (
    <>
      <GlasTooltipSchicht />
      <nav aria-label="Pfad" className="flex items-center" style={{ gap: 12 }}>
        <button type="button" onClick={onBack} data-tip="Zurück zur Ticket-Liste"
          className="immo-glas inline-flex items-center" style={{ height: 36, padding: '0 14px 0 10px', gap: 6, borderRadius: 18, border: 'none', cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800, color: 'var(--ink)' }}>
          <CaretLeft size={14} weight="bold" /> Alle Tickets
        </button>
        <span className="truncate" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Tickets / {ticket.ticket_nummer}</span>
      </nav>

      <div ref={rasterRef} className="grid" style={{ gap: 16, alignItems: 'start', gridTemplateColumns: zweispaltig ? 'minmax(0,1fr) 380px' : 'minmax(0,1fr)',
        ...(zweispaltig ? { marginBottom: -48 } : {}) }}>
        {spalte(<>
          <DetailKopf
            ticket={ticket}
            heute={heute}
            nurIcons={werkzeugNurIcons(viewport.width)}
            mobil={mobil}
            bearbeiten={b.editing}
            titelEntwurf={b.editData.titel}
            onTitelEntwurf={titel => b.setEditData({ ...b.editData, titel })}
            speichertGerade={b.saving}
            beauftragenLaedt={beauftragenLaedt}
            aktionLaeuft={aktionLaeuft || archiving}
            onBearbeiten={() => b.setEditing(true)}
            onAbbrechen={() => b.setEditing(false)}
            onFertig={() => { if (!b.editData.titel.trim()) { zeigeToast('Titel darf nicht leer sein', true); return; } void b.handleSave(); }}
            onAngebot={() => { void handleBeauftragen('angebot'); }}
            onBeauftragen={() => { void handleBeauftragen('auftrag'); }}
            onPdf={() => { void handleExportPdf(); }}
            onPapierkorb={() => setShowArchivConfirm(true)}
            onZurueckholen={() => { void handleRestore(); }}
            onErledigt={() => b.handleStatusChange('erledigt')}
            onHauptaktion={(art, ziel) => { void hauptaktion(art, ziel); }}
            onObjekt={onNavigateToObjekt}
            onMieter={onNavigateToMieter}
          />

          {/* Verlauf (scrollt intern) + Antwortfeld – Gestaltung folgt in E3 */}
          <div ref={verlaufRef} className="immo-scroll flex min-h-0 flex-col overflow-y-auto" style={{ gap: 12, flex: zweispaltig ? '1 1 0' : undefined }}>
            {b.editing && (
              <section className="immo-glas flex flex-col" style={{ borderRadius: 22, padding: 14, gap: 10 }} aria-label="Beschreibung bearbeiten">
                <div className="flex items-center justify-between" style={{ gap: 8 }}>
                  <span className="inline-flex items-center" style={{ gap: 6, fontSize: 12.5, fontWeight: 800, color: 'var(--ink3)' }}>
                    <FileText size={13} /> Beschreibung
                  </span>
                  <span className="inline-flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--fill2)' }} data-tip="KI-Hilfe – Beschreibung sprachlich glätten lassen">
                    <KiTextHelfer
                      key={ticket.id}
                      aktuellerText={b.editData.beschreibung}
                      kontext={{ ticketId: ticket.id }}
                      onUebernehmen={(t) => b.setEditData({ ...b.editData, beschreibung: t })}
                      richtung="unten"
                    />
                  </span>
                </div>
                <ListTextarea glasTips
                  value={b.editData.beschreibung}
                  onChange={(e) => b.setEditData({ ...b.editData, beschreibung: e.target.value })}
                  rows={Math.min(Math.max((b.editData.beschreibung || '').split('\n').length + 1, 6), 20)}
                  className="w-full resize-y"
                  style={{ borderRadius: 14, padding: '12px 14px', border: 'none', outline: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1.5px var(--acc)', color: 'var(--ink)', fontSize: 14, lineHeight: 1.5, fontFamily: 'inherit' }}
                />
              </section>
            )}
            <section aria-label="Verlauf" className="immo-glas" style={{ borderRadius: 26, padding: '16px 16px 18px', flexShrink: 0 }}>
              <TicketVerlaufThread key={threadKey} ticket={ticket} userProfile={userProfile}
                kiZusammenfassung={<TicketKiPanel ticket={ticket} userProfile={userProfile} onUpdate={reload} />} />
            </section>
          </div>
          {!b.editing && (
            <TicketComposer ticket={ticket} userProfile={userProfile} onSent={handleComposerSent}
              onWarteAufMieter={() => b.setzeStatus('warten_auf_rueckmeldung')} />
          )}
        </>)}

        {spalte(<>
          {/* Widget-Spalte (Handoff §6) – Fristen und Verknüpft ruhen im Bearbeiten-Modus (Felder stehen dann im Status-Widget) */}
          <WidgetStatus ticket={ticket} kategorien={kategorien} teamMembers={teamMembers} teams={teams} namen={namen}
            bearbeiten={b.editing} editData={b.editData} setEditData={b.setEditData}
            onStatus={b.handleStatusChange} onSpeichern={(teil) => { void speichereWidget(teil); }} />
          {!b.editing && (
            <WidgetFristen ticket={ticket} heute={heute} termine={termine} termineLaden={termineLaden} naechsterTerminId={naechsterTerminId}
              onSpeichern={(teil) => { void speichereWidget(teil); }} onTerminNeu={handleTerminAnlegen}
              onTerminOeffnen={onNavigateToTermin} onTerminLoesen={(t) => { void handleTerminLoesen(t); }} />
          )}
          {!b.editing && (
            <WidgetVerknuepft ticket={ticket} teamMembers={teamMembers} teams={teams} meineId={userProfile?.id ?? null}
              einheit={namen.einheit} bearbeiterVorschlag={namen.bearbeiterVorschlag} emails={verknuepfteEmails}
              onSpeichern={(teil) => { void speichereWidget(teil); }} onObjekt={onNavigateToObjekt} onMieter={onNavigateToMieter}
              onEmailLoesen={(id) => { void loeseEmail(id); }} />
          )}
          {hatVorgang(ticket) && (
            <WidgetHuelle titel="Vorgang" icon={FlowArrow} style={{ flexShrink: 0 }}>
              <VorgangWidget ticket={ticket} onUpdate={reload} />
            </WidgetHuelle>
          )}
          <WidgetEigentuemerPortal key={`ep-${ticket.id}`} ticketId={ticket.id} userId={userProfile?.id} />
          <WidgetEinsatz key={ticket.id} sprungZuZeiten={einsatzSprung} ticketId={ticket.id} userProfile={userProfile} nachweisKopf={nachweisKopf} />
          <WidgetHuelle titel="Anhänge" icon={Paperclip} style={{ flexShrink: 0 }}
            rechts={anhangAnzahl > 0 ? `${anhangAnzahl} ${anhangAnzahl === 1 ? 'Datei' : 'Dateien'}` : undefined}>
            <TicketAnhaenge ticketId={ticket.id} ticketNummer={ticket.ticket_nummer} userProfile={userProfile} onUpdate={reload} onCountChange={setAnhangAnzahl} />
          </WidgetHuelle>
          <WidgetHuelle titel="Verknüpfungen" icon={LinkSimple} kopf="kachel" style={{ flexShrink: 0 }}>
            <TicketVerknuepfungen ticket={ticket} userProfile={userProfile} onUpdate={reload} />
          </WidgetHuelle>
        </>, true)}
      </div>

      {/* Abschluss-Dialog (Erledigt/Geschlossen verlangen einen Grund) */}
      {b.showAbschluss && (
        <AbschlussDialog ticketNummer={ticket.ticket_nummer} zielStatus={b.abschlussZiel}
          onAbbrechen={() => b.setShowAbschluss(false)}
          onAbschliessen={grund => { b.setAbschlussGrund(grund); b.handleAbschlussMitGrund(grund); }} />
      )}

      {showArchivConfirm && (
        <BestaetigenDialog titel="In Papierkorb verschieben" icon={TrashIcon} bestaetigenLabel="Verschieben" laeuft={archiving}
          text={`Ticket ${ticket.ticket_nummer} wird in den Papierkorb verschoben und aus den Listen ausgeblendet. Du kannst es jederzeit über die Ansicht „Papierkorb" wiederherstellen.`}
          onBestaetigen={() => { void handleArchive(); }} onAbbrechen={() => setShowArchivConfirm(false)} />
      )}
      <SchwebenderToast toast={toast} mobil={mobil} />
    </>
  );
}
